import React, { useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { APIContext } from "../../api/api";
import {
  istDateKey,
  istRangeStart,
  formatISTDateTime,
  formatCalendarDate,
} from "../../lib/admin-time";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import { Mail, RefreshCw, Download, ArrowUpRight, X } from "lucide-react";
import "./email-analytics.css";
import CheckoutReminderAccounts from "./CheckoutReminderAccounts";
import { emailPreviewDocument } from "./email-preview";
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend
);
const number = (n) => Number(n || 0).toLocaleString("en-IN");
const percent = (n) => (n == null ? "—" : `${n.toFixed(1)}%`);
const CHECKOUT_TYPE = "checkout_incomplete_day1";
const label = (s) =>
  s === CHECKOUT_TYPE
    ? "Checkout reminder · Day 1"
    : String(s || "")
        .replace(/_/g, " ")
        .replace(/^./, (c) => c.toUpperCase());
const knownTypes = [
  CHECKOUT_TYPE,
  "welcome",
  "website_ready",
  "new_project",
  "domain_connected",
  "payment_failed",
  "publish_trial_notice",
  "publish_trial_ending",
  "publish_trial_ended",
  "transactional",
];
const initialFilters = () => ({
  startDate: istRangeStart(30),
  endDate: istDateKey(),
  q: "",
  type: "",
  status: "",
});
const options = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: {
      position: "bottom",
      labels: { usePointStyle: true, boxWidth: 8, padding: 20 },
    },
  },
  scales: {
    x: { grid: { display: false }, ticks: { maxTicksLimit: 8 } },
    y: {
      beginAtZero: true,
      ticks: { precision: 0 },
      border: { display: false },
    },
  },
};
function Stat({ title, value, sub }) {
  return (
    <div className="email-stat">
      <span>{title}</span>
      <strong>{value}</strong>
      <small>{sub}</small>
    </div>
  );
}
function Detail({ id, api, onClose }) {
  const dialog = useRef();
  const [preview, setPreview] = useState(false);
  const [loadImages, setLoadImages] = useState(false);
  const [data, setData] = useState(null),
    [error, setError] = useState("");
  useEffect(() => {
    const node = dialog.current;
    node.showModal();
    return () => node.close();
  }, []);
  useEffect(() => {
    let active = true;
    api(`/emails/${id}`)
      .then((r) => {
        if (active) setData(r);
      })
      .catch(() => {
        if (active) setError("Could not load this email. Close and try again.");
      });
    return () => {
      active = false;
    };
  }, [id, api]);
  const email = data?.email;
  const previewHTML = React.useMemo(
    () =>
      preview && data?.content?.html
        ? emailPreviewDocument(data.content.html, loadImages)
        : "",
    [preview, data?.content?.html, loadImages]
  );
  return (
    <dialog
      className="email-detail"
      ref={dialog}
      onCancel={onClose}
      aria-labelledby="email-detail-title"
    >
      <header>
        <div>
          <p className="email-eyebrow">MESSAGE DETAILS</p>
          <h2 id="email-detail-title">{email?.subject || "Email timeline"}</h2>
        </div>
        <button aria-label="Close email details" onClick={onClose}>
          <X size={20} />
        </button>
      </header>
      {error ? (
        <p role="alert">{error}</p>
      ) : !data ? (
        <p role="status">Loading email…</p>
      ) : (
        <>
          <dl>
            <dt>Recipient</dt>
            <dd>{email.recipient}</dd>
            <dt>From</dt>
            <dd>{email.sender}</dd>
            <dt>Email type</dt>
            <dd>{label(email.email_type)}</dd>
            <dt>Status</dt>
            <dd>{label(email.status)}</dd>
            <dt>Attempted</dt>
            <dd>{formatISTDateTime(email.attempted_at)}</dd>
            <dt>Accepted by provider</dt>
            <dd>{formatISTDateTime(email.accepted_at)}</dd>
            <dt>Tracking</dt>
            <dd>
              {email.tracking_enabled ? "Enabled" : "Unavailable for this send"}
            </dd>
            {email.user_id && (
              <>
                <dt>User</dt>
                <dd>
                  <Link to={`/users/${encodeURIComponent(email.user_id)}`}>
                    {email.user_id}
                  </Link>
                </dd>
              </>
            )}
            {email.project_id && (
              <>
                <dt>Website</dt>
                <dd>
                  <Link
                    to={`/projects/${encodeURIComponent(email.project_id)}`}
                  >
                    {email.project_id}
                  </Link>
                </dd>
              </>
            )}
            {email.failure_code && (
              <>
                <dt>Send failure</dt>
                <dd>{email.failure_code}</dd>
              </>
            )}
          </dl>
          {data.checkout && (
            <section
              className="checkout-email-context"
              aria-label="Checkout context"
            >
              <h3>Checkout and account</h3>
              <dl>
                <dt>Latest checkout</dt>
                <dd>{data.checkout.checkout_id}</dd>
                <dt>Selected plan</dt>
                <dd>{data.checkout.plan}</dd>
                <dt>Checkout status</dt>
                <dd>{label(data.checkout.status)}</dd>
                <dt>Current plan</dt>
                <dd>{data.checkout.current_plan || "Free"}</dd>
                <dt>Upgraded</dt>
                <dd>{formatISTDateTime(data.checkout.upgraded_at)}</dd>
                <dt>Opted out</dt>
                <dd>{formatISTDateTime(data.checkout.opted_out_at)}</dd>
              </dl>
            </section>
          )}
          <div className="email-preview-actions">
            <button
              className="email-button"
              aria-expanded={preview}
              aria-controls="email-content-preview"
              onClick={() => setPreview((value) => !value)}
            >
              <Mail size={16} />
              {preview ? "Hide preview" : "Preview email"}
            </button>
          </div>
          {preview && (
            <section id="email-content-preview" aria-label="Email content">
              {previewHTML ? (
                <>
                  <label className="email-preview-images">
                    <input
                      type="checkbox"
                      checked={loadImages}
                      onChange={(event) => setLoadImages(event.target.checked)}
                    />
                    Load remote images
                  </label>
                  <iframe
                    className="email-preview-frame"
                    title="Sent email preview"
                    srcDoc={previewHTML}
                    sandbox=""
                    referrerPolicy="no-referrer"
                  />
                </>
              ) : data.content?.text ? (
                <pre className="email-preview-text">{data.content.text}</pre>
              ) : (
                <p className="email-notice">
                  Preview unavailable. The content of this email was not saved.
                </p>
              )}
            </section>
          )}
          <div className="email-detail-metrics">
            <Stat
              title="Recorded opens"
              value={number(email.open_count)}
              sub={`First: ${formatISTDateTime(email.first_open_at)}`}
            />
            <Stat
              title="Recorded clicks"
              value={number(email.click_count)}
              sub={`First: ${formatISTDateTime(email.first_click_at)}`}
            />
          </div>
          <h3>Event timeline</h3>
          <p className="email-muted">
            Latest events first.{" "}
            {data.totalEvents > 200
              ? `Showing the latest 200 of ${number(data.totalEvents)} events.`
              : ""}
          </p>
          {!data.events.length ? (
            <p className="email-empty">
              No SES events received for this email yet.
            </p>
          ) : (
            <ol className="email-timeline">
              {data.events.map((event, i) => (
                <li key={i}>
                  <div>
                    <strong>{label(event.event_type)}</strong>
                    <time>{formatISTDateTime(event.occurred_at)}</time>
                  </div>
                  {event.details?.bot === "Likely" && (
                    <p>
                      Likely automated activity · excluded from engagement rates
                    </p>
                  )}
                  {event.details?.link && (
                    <p className="email-link-path">{event.details.link}</p>
                  )}
                  {[
                    "bounceType",
                    "bounceSubType",
                    "complaintType",
                    "delayType",
                    "reason",
                  ]
                    .filter((k) => event.details?.[k])
                    .map((k) => (
                      <p key={k}>{event.details[k]}</p>
                    ))}
                </li>
              ))}
            </ol>
          )}
        </>
      )}
    </dialog>
  );
}
export default function EmailAnalytics({ checkoutOnly = false }) {
  const { getEmailAnalytics: api } = useContext(APIContext);
  const [draft, setDraft] = useState(() => ({
      ...initialFilters(),
      type: checkoutOnly ? CHECKOUT_TYPE : "",
    })),
    [filters, setFilters] = useState(() => ({
      ...initialFilters(),
      type: checkoutOnly ? CHECKOUT_TYPE : "",
    }));
  const [page, setPage] = useState(1),
    [refresh, setRefresh] = useState(0),
    [overview, setOverview] = useState(null),
    [logs, setLogs] = useState(null);
  const [busy, setBusy] = useState(true),
    [logBusy, setLogBusy] = useState(true),
    [error, setError] = useState(""),
    [logError, setLogError] = useState("");
  const [selected, setSelected] = useState(null),
    [exporting, setExporting] = useState(false),
    [exportError, setExportError] = useState("");
  useEffect(() => {
    let active = true;
    setBusy(true);
    setError("");
    setOverview(null);
    api("", filters)
      .then((r) => {
        if (active) setOverview(r);
      })
      .catch((err) => {
        if (active)
          setError(
            err.response?.data?.error ||
              "Could not load email analytics. Please try again."
          );
      })
      .finally(() => {
        if (active) setBusy(false);
      });
    return () => {
      active = false;
    };
  }, [api, filters, refresh]);
  useEffect(() => {
    let active = true;
    setLogBusy(true);
    setLogError("");
    setLogs(null);
    api("/emails", { ...filters, page })
      .then((r) => {
        if (active) setLogs(r);
      })
      .catch((err) => {
        if (active)
          setLogError(
            err.response?.data?.error ||
              "Could not load email logs. Please try again."
          );
      })
      .finally(() => {
        if (active) setLogBusy(false);
      });
    return () => {
      active = false;
    };
  }, [api, filters, page, refresh]);
  async function download() {
    setExporting(true);
    setExportError("");
    try {
      const blob = await api("/export", filters, true),
        url = URL.createObjectURL(blob),
        a = document.createElement("a");
      a.href = url;
      a.download = `emails-${filters.startDate}-${filters.endDate}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      let message = "Export failed. Please try again.";
      try {
        message = JSON.parse(await err.response.data.text()).error || message;
      } catch (_) {}
      setExportError(message);
    } finally {
      setExporting(false);
    }
  }
  const s = overview?.summary || {},
    days = overview?.daily || [],
    chartLabels = days.map((d) => formatCalendarDate(d.day));
  const types = [
    ...new Set([
      ...knownTypes,
      ...(overview?.types || []).map((t) => t.email_type),
    ]),
  ];
  const input = (key, value) => setDraft((d) => ({ ...d, [key]: value }));
  const apply = (e) => {
    e.preventDefault();
    setPage(1);
    setFilters({ ...draft });
    setExportError("");
  };
  return (
    <div className="email-analytics">
      <header className="email-page-header">
        <div>
          <p className="email-eyebrow">
            <Mail size={14} /> CUSTOMER COMMUNICATIONS
          </p>
          <h1>{checkoutOnly ? "Checkout reminders" : "Email analytics"}</h1>
          <p className="email-muted">
            {checkoutOnly
              ? "Day-one reminders for incomplete normal-plan checkouts."
              : "Every send, delivery, and customer interaction in one place."}
          </p>
        </div>
        <button
          className="email-button"
          onClick={() => setRefresh((n) => n + 1)}
          disabled={busy || logBusy}
        >
          <RefreshCw size={16} /> Refresh
        </button>
      </header>
      <nav className="email-view-links" aria-label="Email reports">
        <Link
          to="/email-analytics"
          aria-current={!checkoutOnly ? "page" : undefined}
        >
          All emails
        </Link>
        <Link
          to="/checkout-reminders"
          aria-current={checkoutOnly ? "page" : undefined}
        >
          Checkout reminders
        </Link>
      </nav>
      <form className="email-filters" onSubmit={apply}>
        <label>
          From
          <input
            required
            type="date"
            value={draft.startDate}
            max={draft.endDate}
            onChange={(e) => input("startDate", e.target.value)}
          />
        </label>
        <label>
          To
          <input
            required
            type="date"
            value={draft.endDate}
            min={draft.startDate}
            max={istDateKey()}
            onChange={(e) => input("endDate", e.target.value)}
          />
        </label>
        <label>
          Email type
          <select
            disabled={checkoutOnly}
            value={draft.type}
            onChange={(e) => input("type", e.target.value)}
          >
            <option value="">All types</option>
            {types.map((t) => (
              <option key={t} value={t}>
                {label(t)}
              </option>
            ))}
          </select>
        </label>
        <label>
          Status
          <select
            value={draft.status}
            onChange={(e) => input("status", e.target.value)}
          >
            <option value="">All statuses</option>
            {[
              "pending",
              "sent",
              "delivered",
              "opened",
              "clicked",
              "unopened",
              "bounced",
              "complained",
              "delayed",
              "failed",
              "rejected",
              "untracked",
            ].map((t) => (
              <option key={t} value={t}>
                {label(t)}
              </option>
            ))}
          </select>
        </label>
        <label className="email-search">
          Search
          <input
            type="search"
            placeholder="Recipient, subject, user or website ID"
            maxLength={200}
            value={draft.q}
            onChange={(e) => input("q", e.target.value)}
          />
        </label>
        <button className="email-button email-primary" type="submit">
          Apply filters
        </button>
      </form>
      {error && (
        <div role="alert" className="email-error">
          {error}
        </div>
      )}
      {busy ? (
        <div className="email-loading" role="status">
          Loading email analytics…
        </div>
      ) : (
        overview && (
          <>
            {!overview.health.last_event_at && (
              <div className="email-notice">
                No SES events have arrived yet. Delivery and engagement metrics
                become available after event tracking is connected and emails
                are sent.
              </div>
            )}
            <div className="email-stats">
              <Stat
                title="Emails sent"
                value={number(s.sent)}
                sub={`${number(s.attempted)} recipient-level attempts`}
              />
              <Stat
                title="Delivered"
                value={number(s.delivered)}
                sub={`${percent(s.delivery_rate)} of monitored sends`}
              />
              <Stat
                title="Open rate"
                value={percent(s.open_rate)}
                sub={`${number(s.tracked_opened)} of ${number(
                  s.tracked_delivered
                )} tracked deliveries`}
              />
              <Stat
                title="Click rate"
                value={percent(s.click_rate)}
                sub={`${number(s.tracked_clicked)} emails with a click`}
              />
              <Stat
                title="Bounced"
                value={number(s.bounced)}
                sub={`${percent(s.bounce_rate)} of monitored sends`}
              />
              <Stat
                title="Complaints"
                value={number(s.complained)}
                sub={`${percent(s.complaint_rate)} of monitored sends`}
              />
            </div>
            {checkoutOnly && overview.checkoutOutcomes && (
              <>
                <div className="email-stats checkout-outcome-stats">
                  <Stat
                    title="Upgraded after reminder"
                    value={number(
                      overview.checkoutOutcomes.upgraded_after_send
                    )}
                    sub="Recorded upgrade after email acceptance"
                  />
                  <Stat
                    title="Post-reminder upgrade rate"
                    value={percent(overview.checkoutOutcomes.upgrade_rate)}
                    sub={`Of ${number(
                      overview.checkoutOutcomes.linked_sent
                    )} linked sends`}
                  />
                  <Stat
                    title="Opted out after reminder"
                    value={number(
                      overview.checkoutOutcomes.opted_out_after_send
                    )}
                    sub="Stopped checkout reminders"
                  />
                </div>
                <p className="email-method">
                  These outcomes use the filtered send cohort. An upgrade after
                  a reminder does not prove the email caused the purchase.
                </p>
              </>
            )}
            <div className="email-chart-grid">
              <section className="email-panel">
                <h2>Daily email volume</h2>
                <p className="email-muted">
                  Sent and delivered emails, grouped by send attempt date.
                </p>
                <div className="email-chart">
                  <Bar
                    options={options}
                    data={{
                      labels: chartLabels,
                      datasets: [
                        {
                          label: "Sent",
                          data: days.map((d) => d.sent),
                          backgroundColor: "#7652e8",
                          borderRadius: 4,
                        },
                        {
                          label: "Delivered",
                          data: days.map((d) => d.delivered),
                          backgroundColor: "#a5ddc9",
                          borderRadius: 4,
                        },
                      ],
                    }}
                  />
                </div>
              </section>
              <section className="email-panel">
                <h2>Engagement over time</h2>
                <p className="email-muted">
                  Unique engaged emails ÷ tracked deliveries for each send date.
                </p>
                <div className="email-chart">
                  <Line
                    options={{
                      ...options,
                      scales: {
                        ...options.scales,
                        y: {
                          min: 0,
                          max: 100,
                          ticks: { callback: (v) => `${v}%` },
                        },
                      },
                    }}
                    data={{
                      labels: chartLabels,
                      datasets: [
                        {
                          label: "Open rate",
                          data: days.map((d) => d.open_rate),
                          borderColor: "#7652e8",
                          backgroundColor: "#7652e8",
                          tension: 0.25,
                          pointRadius: 2,
                        },
                        {
                          label: "Click rate",
                          data: days.map((d) => d.click_rate),
                          borderColor: "#14977c",
                          backgroundColor: "#14977c",
                          tension: 0.25,
                          pointRadius: 2,
                        },
                      ],
                    }}
                  />
                </div>
              </section>
            </div>
            <section className="email-panel">
              <div className="email-section-title">
                <h2>Performance by email type</h2>
                <span className="email-muted">
                  {number(s.failed)} failed · {number(s.rejected)} rejected ·{" "}
                  {number(s.pending)} pending · {number(s.delayed)} delayed
                </span>
              </div>
              <div className="email-table-scroll">
                <table>
                  <thead>
                    <tr>
                      {[
                        "Email type",
                        "Sent",
                        "Delivered",
                        "Opened",
                        "Open rate",
                        "Click rate",
                        "Bounced",
                      ].map((h) => (
                        <th key={h}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {overview.types.map((t) => (
                      <tr key={t.email_type}>
                        <td>{label(t.email_type)}</td>
                        <td>{number(t.sent)}</td>
                        <td>{number(t.delivered)}</td>
                        <td>{number(t.opened)}</td>
                        <td>{percent(t.open_rate)}</td>
                        <td>{percent(t.click_rate)}</td>
                        <td>{number(t.bounced)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!overview.types.length && (
                <p className="email-empty">
                  No emails match this period and filters.
                </p>
              )}
            </section>
            <p className="email-method">
              Open and click rates count each email once and exclude events SES
              marks as likely automated. Privacy protection and image blocking
              can still affect accuracy. Untracked emails are excluded from
              engagement rates; “unopened” means no recorded open. Tracking
              enabled on {number(s.tracked_sent)} of {number(s.sent)} sent
              emails. Delivery, bounce, and complaint rates use sends with
              delivery monitoring. Dates use IST.
            </p>
          </>
        )
      )}
      <section className="email-panel email-log">
        <div className="email-section-title">
          <div>
            <h2>Recipient log</h2>
            <p className="email-muted">
              {logs ? `${number(logs.total)} emails` : "Emails"} · Select an
              email to preview its content and inspect its timeline.
            </p>
          </div>
          <button
            className="email-button"
            onClick={download}
            disabled={exporting || logBusy || !logs?.total}
          >
            <Download size={16} />
            {exporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
        {exportError && (
          <p role="alert" className="email-error">
            {exportError}
          </p>
        )}
        {logError && (
          <p role="alert" className="email-error">
            {logError}
          </p>
        )}
        {logBusy ? (
          <p role="status" className="email-empty">
            Loading recipient log…
          </p>
        ) : (
          logs && (
            <>
              <div className="email-table-scroll">
                <table>
                  <thead>
                    <tr>
                      {[
                        "Recipient / email",
                        "Sent at",
                        "Status",
                        "Opens",
                        "Clicks",
                        "",
                      ].map((h, i) => (
                        <th key={i}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {logs.emails.map((e) => (
                      <tr key={e.id}>
                        <td>
                          <button
                            className="email-recipient"
                            onClick={() => setSelected(e.id)}
                          >
                            {e.recipient}
                          </button>
                          <span className="email-subject">{e.subject}</span>
                          <small>{label(e.email_type)}</small>
                        </td>
                        <td className="email-nowrap">
                          {formatISTDateTime(e.accepted_at || e.attempted_at)}
                          {!e.accepted_at && <small>Attempted</small>}
                        </td>
                        <td>
                          <span
                            className={`email-status email-status-${e.status}`}
                          >
                            {label(e.status)}
                          </span>
                          {!e.tracking_enabled && <small>Untracked</small>}
                        </td>
                        <td>
                          {e.tracking_enabled ? number(e.open_count) : "—"}
                          {e.first_open_at && (
                            <small>{formatISTDateTime(e.first_open_at)}</small>
                          )}
                        </td>
                        <td>
                          {e.tracking_enabled ? number(e.click_count) : "—"}
                        </td>
                        <td>
                          <button
                            aria-label={`View email to ${e.recipient}`}
                            className="email-open"
                            onClick={() => setSelected(e.id)}
                          >
                            <ArrowUpRight size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!logs.emails.length && (
                <p className="email-empty">
                  No emails found. Try a different period or filter.
                </p>
              )}
              <footer className="email-pagination">
                <span>
                  Page {page} of {Math.max(1, Math.ceil(logs.total / 25))}
                </span>
                <div>
                  <button
                    className="email-button"
                    disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Previous
                  </button>
                  <button
                    className="email-button"
                    disabled={page * 25 >= logs.total}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              </footer>
            </>
          )
        )}
      </section>
      {checkoutOnly && (
        <CheckoutReminderAccounts
          api={api}
          filters={filters}
          refresh={refresh}
          onEmail={setSelected}
        />
      )}
      {overview && (
        <p className="email-method">
          Logging since {formatISTDateTime(overview.health.logging_since)} ·
          Last SES event received{" "}
          {formatISTDateTime(overview.health.last_event_at)}. Earlier email
          history is not reconstructed.
        </p>
      )}
      {selected && (
        <Detail
          key={selected}
          id={selected}
          api={api}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
